import React, { useCallback } from 'react';
import {
  Box,
  Button,
  ButtonSelect,
  MenuMulti,
  Typography,
} from '@saviynt/design-system';

import type { MenuMultiOption } from '@saviynt/design-system';

import './DashboardFilterPills.css';

interface FilterOption {
  label: string;
  value: string;
}

interface DashboardFilterPillsProps {
  isVisible: boolean;
  activeFilters: {
    applications: string[];
    entitlements: string[];
  };
  availableFilters: {
    applications: FilterOption[];
    entitlements: FilterOption[];
  };
  isLoading: boolean;
  onClearAll: () => void;
  onSetFilter: (
    category: 'applications' | 'entitlements',
    values: string[]
  ) => void;
}

function DashboardFilterPills({
  isVisible,
  activeFilters,
  availableFilters,
  isLoading,
  onClearAll,
  onSetFilter,
}: DashboardFilterPillsProps): JSX.Element {
  const hasActiveFilters =
    activeFilters.applications.length > 0 ||
    activeFilters.entitlements.length > 0;

  const handleApplicationsChange = useCallback(
    (selectedOptions?: MenuMultiOption[]) => {
      if (!selectedOptions) {
        return;
      }

      const selectedValues = selectedOptions.map((option) => option.value);

      onSetFilter('applications', selectedValues);
    },
    [onSetFilter]
  );

  const handleEntitlementsChange = useCallback(
    (selectedOptions?: MenuMultiOption[]) => {
      if (!selectedOptions) {
        return;
      }

      const selectedValues = selectedOptions.map((option) => option.value);

      onSetFilter('entitlements', selectedValues);
    },
    [onSetFilter]
  );

  return (
    <Box
      className={`Rules-DashboardFilterPills ${
        isVisible
          ? 'Rules-DashboardFilterPills--isVisible'
          : 'Rules-DashboardFilterPills--isHidden'
      }`}
      aria-hidden={!isVisible}>
      {isLoading ? (
        <Typography kind='body2' className='Rules-DashboardFilterPills-loading'>
          Loading filters...
        </Typography>
      ) : (
        <>
          {/* {hasActiveFilters && (
            <Box className='Rules-DashboardFilterPills-header'>
              <button
                type='button'
                className='Rules-DashboardFilterPills-clearAll'
                onClick={onClearAll}>
                Clear
              </button>
            </Box>
          )} */}

          {/* Filter Selection Menus */}
          <Box className='Rules-DashboardFilterPills-menus'>
            {availableFilters.applications.length > 0 && (
              <Box className='Rules-DashboardFilterPills-menu'>
                <MenuMulti
                  options={availableFilters.applications.map((option) => ({
                    ...option,
                    isChecked: activeFilters.applications.includes(
                      option.value
                    ),
                  }))}
                  label='Applications'
                  onChange={handleApplicationsChange}
                  trigger={<ButtonSelect kind='pill' label='Applications' />}
                  size='medium'
                  isPortalEnabled
                  classNames={{
                    dropdownClassName:
                      'Rules-DashboardFilterPills-menuMultiDropdown',
                  }}
                />
              </Box>
            )}

            {availableFilters.entitlements.length > 0 && (
              <Box className='Rules-DashboardFilterPills-menu'>
                <MenuMulti
                  options={availableFilters.entitlements.map((option) => ({
                    ...option,
                    isChecked: activeFilters.entitlements.includes(
                      option.value
                    ),
                  }))}
                  label='Entitlements'
                  onChange={handleEntitlementsChange}
                  trigger={<ButtonSelect kind='pill' label='Entitlements' />}
                  size='medium'
                  isPortalEnabled
                  classNames={{
                    dropdownClassName:
                      'Rules-DashboardFilterPills-menuMultiDropdown',
                  }}
                />
              </Box>
            )}

            {hasActiveFilters && (
              <Button
                className='Rules-DashboardFilterPills-clearAll'
                onClick={onClearAll}
                kind='ghost'
                size='small'>
                Clear
              </Button>
            )}
          </Box>

          {availableFilters.applications.length === 0 &&
            availableFilters.entitlements.length === 0 && (
              <Typography
                kind='body2'
                className='Rules-DashboardFilterPills-empty'>
                No filters available or they failed to load.
              </Typography>
            )}
        </>
      )}
    </Box>
  );
}

export default DashboardFilterPills;
